import React, { createContext, useContext } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { twJoin, twMerge } from "tailwind-merge";

import { Avatar } from "src/design-system/Avatar";
import { IconProps } from "src/design-system/Icon";
import { getSize } from "src/design-system/utils/sizes";

// Link to the figma file with the variants and sizes: https://www.figma.com/file/M6oWoeOynJBBK0xppg19h9/Decide-Redesign%3A-Deliverables?node-id=2406%3A10113&t=VPSpQ2WFCRjDtNU5-0

type PillIconProps = {
  onClick?: (event: React.MouseEvent) => void;
} & Pick<IconProps, "spin" | "icon">;

type PillTextProps = {
  children: React.ReactNode;
};

type PillFontType = "text" | "code" | "code-12";

type PillAvatarProps = React.ComponentProps<typeof Avatar>;

type SubComponents = {
  Text: React.FC<PillTextProps>;
  Icon: React.FC<PillIconProps>;
  Avatar: React.FC<PillAvatarProps>;
};

export type PillVariants =
  | "indigo-light"
  | "red"
  | "gray"
  | "yellow"
  | "green"
  | "indigo"
  | "orange"
  | "outlined-white"
  | "outlined-gray"
  | "dark-gray";

const PILL_VARIANT_CLASSES: Record<PillVariants, string> = {
  "indigo-light": "border-indigo-50 bg-indigo-50 text-indigo-500",
  red: "border-red-50 bg-red-50 text-red-500",
  gray: "border-gray-100 bg-gray-100 text-gray-600",
  yellow: "border-yellow-50 bg-yellow-50 text-yellow-600",
  green: "border-green-50 bg-green-50 text-green-700",
  indigo: "border-indigo-500 bg-indigo-500 text-white",
  orange: "border-orange-50 bg-orange-50 text-orange-600",
  "outlined-white": "border-gray-200 bg-white text-gray-600",
  "outlined-gray": "border-gray-300 bg-gray-100 text-gray-800",
  "dark-gray": "border-gray-700 bg-gray-700 text-gray-50",
};

const resolvePillVariantClasses = (variant: PillVariants): string =>
  PILL_VARIANT_CLASSES[variant];

const PILL_ICON_VARIANT_CLASSES: Record<PillVariants, string> = {
  "indigo-light": "text-indigo-400",
  red: "text-red-400",
  gray: "text-gray-400",
  yellow: "text-yellow-400",
  green: "text-green-400",
  indigo: "text-white",
  orange: "text-orange-600",
  "outlined-white": "text-gray-500",
  "outlined-gray": "text-gray-800",
  "dark-gray": "text-gray-50",
};

const resolvePillIconColor = (variant: PillVariants): string =>
  PILL_ICON_VARIANT_CLASSES[variant];

const PILL_VARIANTS: Set<string> = new Set<PillVariants>(
  Object.keys(PILL_VARIANT_CLASSES) as PillVariants[],
);

// Code pills render a smaller (8px) icon than regular pills (10px).
const resolvePillIconSize = (fontType: PillFontType) =>
  fontType === "text" ? "2xs" : "3xs";

export const resolvePillVariant = (
  value: string | undefined,
  fallback: PillVariants = "gray",
): PillVariants =>
  value !== undefined && PILL_VARIANTS.has(value)
    ? (value as PillVariants)
    : fallback;

type PillProps = {
  onClick?: (event: React.MouseEvent) => void;
  dataLoc?: string;
  children?: React.ReactNode;
  variant?: PillVariants;
  size?: "sm" | "md";
  fontType?: PillFontType;
  fullWidth?: boolean;
  maxWidth?: boolean;
  hovered?: boolean;
  ref?: React.Ref<HTMLDivElement>;
} & Pick<React.HTMLAttributes<HTMLDivElement>, "tabIndex"> & {
    [key: `data-${string}`]: string | number;
  };

const PillContext = createContext<{
  variant: PillVariants;
  fontType: PillFontType;
} | null>(null);

const usePillContext = (subcomponent: string) => {
  const ctx = useContext(PillContext);
  if (!ctx) {
    throw new Error(`${subcomponent} must be used within a Pill component`);
  }
  return ctx;
};

export const Pill = (({
  children,
  onClick,
  dataLoc,
  variant = "indigo-light",
  size = "md",
  fontType = "text",
  fullWidth = false,
  maxWidth = false,
  hovered = false,
  ref,
  ...props
}: PillProps) => {
  return (
    <div
      ref={ref}
      className={twMerge(
        "inline-flex h-fit items-center whitespace-nowrap border",
        resolvePillVariantClasses(variant),

        size === "sm" && "h-5 rounded px-1",
        size === "md" && "rounded-full px-2 py-px",

        onClick && "cursor-pointer",

        fullWidth && "w-full",
        maxWidth && "max-w-full",

        hovered && "bg-gray-50",
      )}
      data-loc={dataLoc}
      onClick={onClick}
      {...props}
    >
      <PillContext value={{ variant, fontType }}>{children}</PillContext>
    </div>
  );
}) as React.FC<PillProps> & SubComponents;

const PillIcon: React.FC<PillIconProps> = ({ icon, spin, onClick }) => {
  const { variant, fontType } = usePillContext("Pill.Icon");
  const { width, height } = getSize(resolvePillIconSize(fontType));

  const iconElement = (
    <span className="flex p-1">
      <FontAwesomeIcon
        className={twJoin(
          "aspect-square transition-all",
          width,
          height,
          resolvePillIconColor(variant),
        )}
        icon={icon}
        spin={spin}
      />
    </span>
  );

  if (onClick) {
    return (
      <button onClick={onClick} type="button">
        {iconElement}
      </button>
    );
  }

  return iconElement;
};

const PillText: React.FC<PillTextProps> = ({ children }) => {
  const { fontType } = usePillContext("Pill.Text");

  return (
    <span
      className={twJoin(
        "min-w-0 truncate",
        fontType === "text" && "mx-1 font-inter-medium-12px",
        fontType === "code" && "font-fira-code", // deprecated: font-fira-code
        fontType === "code-12" && "font-code-12",
      )}
    >
      {children}
    </span>
  );
};

const PillAvatar: React.FC<PillAvatarProps> = (props) => (
  <Avatar {...props} size="xs" />
);

Pill.Text = PillText;
Pill.Icon = PillIcon;
Pill.Avatar = PillAvatar;
